/**
 * @file src/room/RoomMemberAvatar.tsx
 * @description Somebody in the room, as a picture.
 *
 * Turning a member into a face is room knowledge — the roster, the shared card cache, the fact
 * that a guest has no card at all — so it cannot live in the design library. This composes the
 * library's `Avatar` and adds only that knowledge, once, for every surface that shows a person:
 * the seat list, the control a peer is holding, the sensor toggle, the chat.
 *
 * Take a `clientId` when the caller knows a member of the room (a lease holder, a sensor driver)
 * and a `userId` when it already knows the account (a seat row, a chat message). A client is
 * resolved through the roster to its account; either way the card lookup and the initial fallback
 * are the same, so they are written here rather than at each call site.
 *
 * SIZE is the one thing worth reading before using this. The library's `Avatar` applies its `size`
 * prop as an INLINE style and spreads caller props afterwards, so a class can never win and a
 * `style` prop replaces the dimensions wholesale. That makes a plain number the only safe way to
 * size it from outside — and a fixed pixel size is wrong on a control, which shrinks with the
 * viewport until the badge is wider than the thing it badges. So `size` here also takes a CSS
 * length: a percentage sizes the avatar against whatever box it is placed in, and it goes through
 * the same inline style the library uses, so nothing has to fight it.
 */
import { Avatar, AvatarFallback, AvatarImage } from 'plantasia.space-design/react';

import { useRoomMemberIdentity, useRoomUserCard, userCardAvatar } from './RoomUserCard';

export interface RoomMemberAvatarProps {
  /** A member of the room, resolved through the roster to their account. */
  clientId?: string | null;
  /** The account directly, when the caller already knows it (a seat row, a chat message). */
  userId?: string | null;
  /** Name to take the fallback initial from when the card has none — a guest, or a card still loading. */
  name?: string | null;
  /** Pixels as a number, or any CSS length: `'30%'` sizes the avatar against the box it sits in. */
  size?: number | string;
  className?: string;
  /**
   * Carry the person's name — as the picture's alt text and as the hover title.
   *
   * Off by default, because most surfaces print the name right next to the face (a seat row, a chat
   * message) and a named avatar there says everything twice. Turn it on where the face is the ONLY
   * thing carrying the person: then it is the difference between "who can play this" listing people
   * and listing nothing at all.
   */
  named?: boolean;
}

/** The letter shown while there is no picture. Guests never get one, so this is their whole face. */
function initialOf(name: string | null | undefined): string {
  return (name?.trim()[0] ?? '?').toUpperCase();
}

export function RoomMemberAvatar({
  clientId,
  userId,
  name,
  size = 32,
  className,
  named = false,
}: RoomMemberAvatarProps) {
  // Only consult the roster when the caller gave a client; a caller that already knows the account
  // passes it straight through. Both paths run every hook, so which one is used never changes.
  const identity = useRoomMemberIdentity(clientId ?? null);
  const resolvedUserId = userId ?? identity?.userId ?? null;
  const card = useRoomUserCard(resolvedUserId, { load: true });
  const src = userCardAvatar(card);
  const label = name || card?.username || identity?.displayName || '';
  const length = typeof size === 'number' ? `${size}px` : size;

  return (
    // The library's own `size` is deliberately not passed: this style REPLACES the one it would
    // set, which is the only way a relative size survives.
    <Avatar
      className={className}
      style={{ width: length, height: length }}
      title={named && label ? label : undefined}
    >
      {src ? <AvatarImage src={src} alt={named ? label : ''} /> : null}
      <AvatarFallback>{initialOf(label)}</AvatarFallback>
    </Avatar>
  );
}
